import styles from './create-new-place-dialog.module.scss';
import { ChangeEvent, useContext, useState } from 'react';
import { Col, Row } from 'reactstrap';
import { makeCourseRequest } from 'libs/webkua-lib/src/lib/requestUtils';
import { Dialog, DialogActions, DialogContent, DialogTitle, GrayOutlinedButton, LoadingButton, SnackBarContext, TextField } from '@frontend-workspace/react-shared-lib';
import { useTranslation } from 'react-i18next';

/* eslint-disable-next-line */
export interface CreateNewPlaceDialogProps {
  onClose: () => void;
  open: boolean,
  refreshData: () => void
}

export function CreateNewPlaceDialog(props: CreateNewPlaceDialogProps) {
  const { t } = useTranslation();
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const [loading, setLoading] = useState<boolean>(false);

  const [name, setName] = useState<string>("");
  const [seats, setSeats] = useState<number>(0);
  const [place, setPlace] = useState<string>("");
  const [address, setAddress] = useState<string>("");
  const [postCode, setPostCode] = useState<string>("");
  const [city, setCity] = useState<string>("");

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => setName(event.target.value);
  const handleSeatsChange = (event: ChangeEvent<HTMLSelectElement>) => setSeats(parseInt(event.target.value));
  const handlePlaceChange = (event: ChangeEvent<HTMLInputElement>) => setPlace(event.target.value);
  const handleAddressChange = (event: ChangeEvent<HTMLInputElement>) => setAddress(event.target.value);
  const handlePostCodeChange = (event: ChangeEvent<HTMLInputElement>) => setPostCode(event.target.value);
  const handleCityChange = (event: ChangeEvent<HTMLInputElement>) => setCity(event.target.value);

  const missingFields = (): boolean => {
    const requiredFieldsFilled =
      name &&
      seats &&
      address &&
      city;
    return requiredFieldsFilled ? false : true;
  };

  const handleAddPlace = () => {
    setLoading(true);
    const body = {
      name: name,
      seats: seats,
      place: place,
      address: address,
      postCode: postCode,
      city: city
    }
    makeCourseRequest(`room/upsert`, 'POST', body).then(result => {
      setSnackbarMessage({ content: t('standard.addedLocationToList'), type: "info", });
      setLoading(false);
      props.refreshData();
      props.onClose();
    }).catch(() => setSnackbarMessage({ content: t('createNewPlace.couldNotAddLocationToList'), type: "error" }));
  };

  return (
    <Dialog
      open={props.open}
      maxWidth="sm"
      fullWidth>
      <DialogTitle className='mb-4'>
        {t('standard.newPlace')}
      </DialogTitle>
      <DialogContent>
        <div className='mb-3'>
          <TextField
            label={<span>{t('standard.name')} &#42;</span>}
            name='name'
            fullWidth
            sx={{ minWidth: 300 }}
            value={name || ''}
            onChange={handleNameChange}
          />
        </div>
        <Row>
          <Col xl={3}>
            <div className='mb-3'>
              <TextField
                label={<span>{t('standard.seats')} &#42;</span>}
                name='seats'
                type='number'
                sx={{ minWidth: 100 }}
                value={seats || ''}
                onChange={handleSeatsChange}
              />
            </div>
          </Col>
        </Row>
        <div className='mb-3'>
          <TextField
            label={t('standard.place')}
            name='place'
            fullWidth
            sx={{ minWidth: 300 }}
            value={place || ''}
            onChange={handlePlaceChange}
          />
        </div>
        <div className='mb-3'>
          <TextField
            label={<span>{t('standard.address')} &#42;</span>}
            name='address'
            fullWidth
            sx={{ minWidth: 300 }}
            value={address || ''}
            onChange={handleAddressChange}
          />
        </div>
        <Row>
          <Col xl={4}>
            <div className='mb-3'>
              <TextField
                label={t('standard.postCode')}
                name='postCode'
                sx={{ minWidth: 100 }}
                value={postCode || ''}
                onChange={handlePostCodeChange}
              />
            </div>
          </Col>
          <Col xl={8}>
            <div className='mb-3'>
              <TextField
                label={<span>{t('standard.city')} &#42;</span>}
                name='city'
                fullWidth
                sx={{ minWidth: 300 }}
                value={city || ''}
                onChange={handleCityChange}
              />
            </div>
          </Col>
        </Row>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          variant="contained"
          size='large'
          onClick={handleAddPlace}
          disabled={loading || missingFields()}
          loading={loading}
          className='minButtonWidth'>
          {loading ? '' : t('createNewPlace.addLocation')}
        </LoadingButton>
        <GrayOutlinedButton
          variant="outlined"
          size='large'
          onClick={props.onClose}
          className='minButtonWidth'>
          {t('standard.cancel')}
        </GrayOutlinedButton>
      </DialogActions>
    </Dialog >
  );
}

export default CreateNewPlaceDialog;
